import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Collapse
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import WarningIcon from '@material-ui/icons/Warning';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import TouchAppIcon from '@material-ui/icons/TouchApp';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoIcon from '@material-ui/icons/Info';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import FolderIcon from '@material-ui/icons/Folder';
import SecurityIcon from '@material-ui/icons/Security';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import BackupIcon from '@material-ui/icons/Backup';
import { transceive, setAdminAuthenticated } from './actions';
import { getDaysSinceLastBackup } from './backupReminder';

const AdminPINRecovery = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();

  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [dataLossAcknowledged, setDataLossAcknowledged] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [showDataLossDetails, setShowDataLossDetails] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canProceed, setCanProceed] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupConfirmed, setBackupConfirmed] = useState(false);

  // 暴露方法给父组件 (Optimization 1)
  useImperativeHandle(ref, () => ({
    openResetDialog: () => {
      setResetDialogOpen(true);
      setActiveStep(0);
      setConfirmText('');
      setUnderstood(false);
      setDataLossAcknowledged(false);
      setShowDataLossDetails(false);
      setBackupConfirmed(false);
    }
  }));

  const steps = ['⚠️ Warning', '📋 Confirm', '👆 Touch Device', '✅ Complete'];

  // Optimization 2: 检查备份状态
  useEffect(() => {
    const daysSinceBackup = getDaysSinceLastBackup();
    setBackupStatus({
      hasBackup: daysSinceBackup !== null,
      daysSinceBackup: daysSinceBackup,
      needsBackup: daysSinceBackup === null || daysSinceBackup > 7
    });
  }, []);

  // Optimization 2: 监听 URL hash 返回
  useEffect(() => {
    if (window.location.hash === '#factory-reset') {
      setResetDialogOpen(true);
      // 直接跳到确认步骤（跳过警告步骤）
      setActiveStep(1);
      window.history.replaceState(null, null, ' ');
    }
  }, []);

  // 数据丢失详情
  const dataLossItems = [
    {
      icon: <VpnKeyIcon color="error" />,
      primary: 'PIV Certificates and Keys',
      secondary: 'All PIV slots (9A, 9C, 9D, 9E) will be erased. You will lose access to systems using these certificates.',
      critical: true
    },
    {
      icon: <SecurityIcon color="error" />,
      primary: 'OpenPGP Keys',
      secondary: 'Signature, Decryption, and Authentication keys will be deleted. Encrypted emails will become unreadable.',
      critical: true
    },
    {
      icon: <FolderIcon color="error" />,
      primary: 'OATH Credentials (2FA)',
      secondary: 'All TOTP/HOTP codes will be lost. You may lose access to accounts if you don\'t have backup codes.',
      critical: true
    },
    {
      icon: <SecurityIcon color="error" />,
      primary: 'FIDO2/WebAuthn Credentials',
      secondary: 'All registered websites and services will need to be re-registered.',
      critical: false
    },
    {
      icon: <FolderIcon color="error" />,
      primary: 'Other Data',
      secondary: 'NDEF data, Pass data, and all other applet configurations.',
      critical: false
    }
  ];

  // 倒计时效果
  React.useEffect(() => {
    if (activeStep === 0 && resetDialogOpen) {
      setCountdown(5);
      setCanProceed(false);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setCanProceed(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [activeStep, resetDialogOpen]);

  const handleOpenResetDialog = () => {
    setResetDialogOpen(true);
    setActiveStep(0);
    setConfirmText('');
    setUnderstood(false);
    setDataLossAcknowledged(false);
    setShowDataLossDetails(false);
  };

  const handleCloseResetDialog = () => {
    if (!processing) {
      setResetDialogOpen(false);
      setConfirmText('');
      setUnderstood(false);
      setDataLossAcknowledged(false);
      setActiveStep(0);
      setShowDataLossDetails(false);
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      // 从警告页面到确认页面
      setActiveStep(1);
    }
  };

  const handleBackStep = () => {
    if (activeStep === 1) {
      setActiveStep(0);
    }
  };

  const handleFactoryReset = async () => {
    try {
      setProcessing(true);
      setActiveStep(2); // Move to "Touch Device" step

      // 1. 选择 Admin applet
      await dispatch(transceive('00A4040005F000000000'));

      // 2. 检查 Admin PIN 是否被锁定
      const verifyResponse = await dispatch(transceive('00200000'));

      if (!verifyResponse.endsWith('6983')) {
        // PIN 未被锁定
        const sw = verifyResponse.slice(-4);
        if (sw.startsWith('63C')) {
          const retries = parseInt(sw.slice(-1), 16);
          enqueueSnackbar(
            `❌ Factory Reset is only available when Admin PIN is blocked. Current retries remaining: ${retries}`,
            { variant: 'error', autoHideDuration: 8000 }
          );
        } else {
          enqueueSnackbar(
            '❌ Factory Reset is only available when Admin PIN is blocked (0 retries left)',
            { variant: 'error', autoHideDuration: 8000 }
          );
        }
        setActiveStep(1);
        return;
      }

      // 3. 发送 Factory Reset 命令
      const resetAPDU = '00500000055245534554';

      const touchPromptKey = enqueueSnackbar(
        '👆 Please touch your device NOW to confirm Factory Reset',
        {
          variant: 'info',
          persist: true,
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        }
      );

      const response = await dispatch(transceive(resetAPDU));

      // 关闭触摸提示
      closeSnackbar(touchPromptKey);

      if (response.endsWith('9000')) {
        setActiveStep(3); // Move to "Complete" step

        enqueueSnackbar(
          '✅ Factory Reset successful! Your device has been reset to factory settings.',
          { variant: 'success', autoHideDuration: 8000 }
        );

        // 清除认证状态
        dispatch(setAdminAuthenticated(false));

        // 延迟关闭对话框
        setTimeout(() => {
          handleCloseResetDialog();

          // 重要提示
          enqueueSnackbar(
            '🔑 Default Admin PIN is now: 123456. Please change it immediately after login!',
            {
              variant: 'warning',
              autoHideDuration: 15000,
              anchorOrigin: { vertical: 'top', horizontal: 'center' }
            }
          );
        }, 3000);

      } else if (response.endsWith('6985')) {
        setActiveStep(1);
        enqueueSnackbar(
          '❌ Factory Reset failed: Conditions not satisfied. Make sure Admin PIN is blocked and you touched the device.',
          { variant: 'error', autoHideDuration: 8000 }
        );
      } else if (response.endsWith('6A80')) {
        setActiveStep(1);
        enqueueSnackbar(
          '❌ Factory Reset failed: Invalid data. Please try again.',
          { variant: 'error', autoHideDuration: 8000 }
        );
      } else {
        setActiveStep(1);
        enqueueSnackbar(
          `❌ Factory Reset failed with status code: ${response.slice(-4)}`,
          { variant: 'error', autoHideDuration: 8000 }
        );
      }
    } catch (error) {
      setActiveStep(1);
      enqueueSnackbar(
        `❌ Factory Reset error: ${error.message}`,
        { variant: 'error', autoHideDuration: 8000 }
      );
    } finally {
      setProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Warning
        return (
          <Box>
            <Alert severity="error" style={{ marginBottom: 24 }}>
              <AlertTitle>
                <Box display="flex" alignItems="center">
                  <ErrorOutlineIcon style={{ marginRight: 8 }} />
                  <strong>⚠️ CRITICAL WARNING: DATA LOSS</strong>
                </Box>
              </AlertTitle>
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Factory Reset will <strong>PERMANENTLY DELETE ALL DATA</strong> on your device.
                This action <strong>CANNOT BE UNDONE</strong>.
              </Typography>
            </Alert>

            <Paper elevation={2} style={{ padding: 16, marginBottom: 16, backgroundColor: '#fff3e0' }}>
              <Box display="flex" alignItems="flex-start">
                <InfoIcon color="primary" style={{ marginRight: 12, marginTop: 4 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>What will happen:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • All certificates, keys, and credentials will be erased<br />
                    • Admin PIN will be reset to default: <strong>123456</strong><br />
                    • Device will return to factory state<br />
                    • You will need to reconfigure everything from scratch
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Box marginBottom={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowDataLossDetails(!showDataLossDetails)}
                endIcon={showDataLossDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                style={{ marginBottom: 16 }}
              >
                {showDataLossDetails ? 'Hide' : 'Show'} Detailed Data Loss Information
              </Button>

              <Collapse in={showDataLossDetails}>
                <Paper elevation={1} style={{ padding: 16, backgroundColor: '#ffebee' }}>
                  <Typography variant="subtitle2" gutterBottom color="error">
                    <strong>⚠️ The following data will be PERMANENTLY DELETED:</strong>
                  </Typography>
                  <List dense>
                    {dataLossItems.map((item, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>{item.icon}</ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                <strong>{item.primary}</strong>
                                {item.critical && <span style={{ color: '#d32f2f' }}> (CRITICAL)</span>}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="textSecondary">
                                {item.secondary}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < dataLossItems.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              </Collapse>
            </Box>

            {/* Optimization 2: 备份状态和引导 */}
            <Alert
              severity={backupStatus?.needsBackup ? "error" : "info"}
              style={{ marginTop: 16, marginBottom: 16 }}
            >
              <AlertTitle>
                {backupStatus?.needsBackup ? "⚠️ No Recent Backup Detected" : "✓ Backup Status"}
              </AlertTitle>
              {backupStatus?.hasBackup ? (
                <Typography variant="body2">
                  Last backup: {backupStatus.daysSinceBackup} days ago
                  {backupStatus.needsBackup && (
                    <strong> - We strongly recommend creating a new backup before proceeding.</strong>
                  )}
                </Typography>
              ) : (
                <Typography variant="body2">
                  <strong>No backup found.</strong> We strongly recommend backing up your data before Factory Reset.
                </Typography>
              )}
            </Alert>

            {/* 备份操作按钮 */}
            <Paper elevation={2} style={{ padding: 16, marginBottom: 16, backgroundColor: '#fff3e0' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>📦 Backup Your Data First</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Create a backup to save your certificates, keys, and credentials before resetting.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BackupIcon />}
                  onClick={() => {
                    // 关闭当前对话框
                    setResetDialogOpen(false);
                    // 跳转到备份页面
                    history.push('/backup?returnTo=admin&action=factory-reset');
                  }}
                >
                  Go to Backup
                </Button>
              </Box>
            </Paper>

            <Alert severity="warning" style={{ marginTop: 16 }}>
              <AlertTitle>Before proceeding, make sure you:</AlertTitle>
              <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                <li>Have backup codes for all 2FA accounts</li>
                <li>Have exported or backed up important certificates</li>
                <li>Understand that encrypted data will become unreadable</li>
                <li>Are prepared to reconfigure your device from scratch</li>
              </ul>
            </Alert>

            {!canProceed && (
              <Box textAlign="center" marginTop={3}>
                <Typography variant="h6" color="error">
                  Please read the warning carefully
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  You can proceed in {countdown} second{countdown !== 1 ? 's' : ''}...
                </Typography>
                <CircularProgress
                  variant="determinate"
                  value={(5 - countdown) * 20}
                  style={{ marginTop: 8 }}
                />
              </Box>
            )}
          </Box>
        );

      case 1: // Confirm
        return (
          <Box>
            <Alert severity="error" style={{ marginBottom: 16 }}>
              <AlertTitle>⚠️ Final Confirmation Required</AlertTitle>
            </Alert>

            <Paper elevation={1} style={{ padding: 16, marginBottom: 16, backgroundColor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Requirements for Factory Reset:</strong>
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Admin PIN must be blocked (0 retries left)"
                    secondary="If you still have retries, you cannot use Factory Reset"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Device must be connected via USB (not NFC)"
                    secondary="For security reasons, Factory Reset only works over USB"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="You must physically touch the device when prompted"
                    secondary="This confirms you have physical access to the device"
                  />
                </ListItem>
              </List>
            </Paper>

            <Divider style={{ margin: '24px 0' }} />

            <Typography variant="body2" paragraph style={{ fontWeight: 500 }}>
              To confirm you understand the consequences, please complete the following:
            </Typography>

            {/* Optimization 2: 备份状态提醒 */}
            {backupStatus?.needsBackup && (
              <Alert severity="warning" style={{ marginBottom: 16 }}>
                <Typography variant="body2">
                  ⚠️ <strong>Warning:</strong> No recent backup detected.
                  Are you sure you want to proceed without backing up your data?
                </Typography>
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={dataLossAcknowledged}
                  onChange={(e) => setDataLossAcknowledged(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  <strong>I understand that ALL DATA will be PERMANENTLY DELETED</strong> and this action cannot be undone
                </Typography>
              }
              style={{ marginBottom: 16, alignItems: 'flex-start' }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={understood}
                  onChange={(e) => setUnderstood(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I have backed up all important data and am prepared to reconfigure my device
                </Typography>
              }
              style={{ marginBottom: 16, alignItems: 'flex-start' }}
            />

            {/* Optimization 2: 备份确认复选框 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={backupConfirmed}
                  onChange={(e) => setBackupConfirmed(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  {backupStatus?.hasBackup && !backupStatus?.needsBackup
                    ? "I have a recent backup of my data"
                    : "I understand I have NO backup and accept the risk of data loss"}
                </Typography>
              }
              style={{ marginBottom: 16, alignItems: 'flex-start' }}
            />

            <TextField
              fullWidth
              label="Type 'DELETE ALL DATA' to confirm (case-sensitive)"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              margin="normal"
              variant="outlined"
              error={confirmText !== '' && confirmText !== 'DELETE ALL DATA'}
              helperText={
                confirmText === 'DELETE ALL DATA'
                  ? '✓ Confirmation text correct'
                  : 'Type exactly: DELETE ALL DATA'
              }
              autoFocus
              inputProps={{
                style: { fontFamily: 'monospace', fontSize: '14px' }
              }}
            />

            <Alert severity="info" style={{ marginTop: 16 }}>
              <Typography variant="caption">
                <strong>After Factory Reset:</strong> Your Admin PIN will be reset to <strong>123456</strong>.
                Please change it immediately after logging in for security.
              </Typography>
            </Alert>
          </Box>
        );

      case 2: // Touch Device
        return (
          <Box style={{ textAlign: 'center', padding: 32 }}>
            <TouchAppIcon style={{ fontSize: 100, color: '#1976d2', marginBottom: 24, animation: 'pulse 1.5s infinite' }} />
            <Typography variant="h5" gutterBottom style={{ fontWeight: 500 }}>
              Touch Your Device Now
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Please touch the sensor on your CanoKey device to confirm Factory Reset.
            </Typography>
            <Typography variant="body2" color="error" paragraph>
              ⚠️ This is your last chance to cancel. Once you touch the device, all data will be erased.
            </Typography>
            <CircularProgress size={60} style={{ marginTop: 24 }} />
            <Typography variant="caption" display="block" style={{ marginTop: 16 }} color="textSecondary">
              Waiting for device touch...
            </Typography>
          </Box>
        );

      case 3: // Complete
        return (
          <Box style={{ textAlign: 'center', padding: 32 }}>
            <CheckCircleIcon style={{ fontSize: 100, color: '#4caf50', marginBottom: 24 }} />
            <Alert severity="success" style={{ marginBottom: 24 }}>
              <AlertTitle>✅ Factory Reset Successful!</AlertTitle>
            </Alert>

            <Typography variant="h6" gutterBottom>
              Your device has been reset to factory settings
            </Typography>

            <Paper elevation={2} style={{ padding: 16, marginTop: 24, backgroundColor: '#e3f2fd' }}>
              <Typography variant="subtitle2" gutterBottom>
                <strong>🔑 Important: Default Credentials</strong>
              </Typography>
              <Typography variant="body1" style={{ fontFamily: 'monospace', fontSize: '18px', margin: '16px 0' }}>
                Admin PIN: <strong>123456</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Please change this PIN immediately after logging in!
              </Typography>
            </Paper>

            <Alert severity="warning" style={{ marginTop: 24 }}>
              <AlertTitle>Next Steps:</AlertTitle>
              <ol style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20, textAlign: 'left' }}>
                <li>Reconnect your device if needed</li>
                <li>Log in with default PIN: <strong>123456</strong></li>
                <li><strong>Change your Admin PIN immediately</strong></li>
                <li>Reconfigure all applets (PIV, OpenPGP, OATH, etc.)</li>
                <li>Re-register with websites and services</li>
              </ol>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card data-recovery-card>
        <CardContent>
          <Box display="flex" alignItems="center" marginBottom={2}>
            <WarningIcon color="error" style={{ marginRight: 8, fontSize: 32 }} />
            <Typography variant="h6">
              🔐 Admin PIN Recovery (Factory Reset)
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary" paragraph>
            If your Admin PIN is blocked (0 retries left), you can reset it using Factory Reset.
          </Typography>

          <Alert severity="error" style={{ marginBottom: 16 }}>
            <AlertTitle>⚠️ Critical Warning</AlertTitle>
            <Typography variant="body2">
              Factory Reset will <strong>PERMANENTLY DELETE ALL DATA</strong> on your device.
              Only use this as a last resort when your Admin PIN is blocked.
            </Typography>
          </Alert>

          <Paper elevation={1} style={{ padding: 16, marginBottom: 16, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>When to use this feature:</strong>
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Admin PIN is blocked (entered wrong PIN 3 times)</li>
              <li>You forgot your Admin PIN and it's now blocked</li>
              <li>You want to completely reset your device to factory state</li>
            </ul>
          </Paper>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>💡 Alternative:</strong> If your Admin PIN is NOT blocked yet, use the "Change PIN" feature instead.
              Factory Reset should only be used when you have no other option.
            </Typography>
          </Alert>
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenResetDialog}
            startIcon={<DeleteForeverIcon />}
            size="large"
          >
            Factory Reset Device
          </Button>
        </CardActions>
      </Card>

      {/* Factory Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCloseResetDialog}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={processing}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ErrorOutlineIcon color="error" style={{ marginRight: 8 }} />
            Factory Reset - Data Loss Warning
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} style={{ marginBottom: 24 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          {activeStep === 0 && (
            <>
              <Button onClick={handleCloseResetDialog} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleNextStep}
                variant="contained"
                color="primary"
                disabled={!canProceed}
              >
                I Understand, Continue
              </Button>
            </>
          )}
          {activeStep === 1 && (
            <>
              <Button onClick={handleBackStep} disabled={processing}>
                Back
              </Button>
              <Button onClick={handleCloseResetDialog} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleFactoryReset}
                color="secondary"
                variant="contained"
                disabled={
                  confirmText !== 'DELETE ALL DATA' ||
                  !understood ||
                  !dataLossAcknowledged ||
                  !backupConfirmed ||
                  processing
                }
                startIcon={<DeleteForeverIcon />}
              >
                Factory Reset Now
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <Button onClick={handleCloseResetDialog} disabled={processing}>
              Cancel
            </Button>
          )}
          {activeStep === 3 && (
            <Button onClick={handleCloseResetDialog} variant="contained" color="primary">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </>
  );
});

export default AdminPINRecovery;
